import { c } from 'ttag';

import { useNotifications } from '@proton/app-context/useNotifications';
import { useModalTwoPromise } from '@proton/components/components/modalTwo/useModalTwo';
import { useLoadingByKey } from '@proton/hooks/useLoading';
import type { FullBillingAddress } from '@proton/payments/core/billing-address/billing-address';

import { usePaymentsApi } from '../../../react-extensions/usePaymentsApi';
import type { TaxCountryHook } from '../hooks/useTaxCountry';
import type { VatNumberHook } from '../hooks/useVatNumber';
import { EditBillingAddressModal, type EditBillingAdressModalInputs } from './EditBillingAddress';

export const useEditBillingAddressModal = () => {
    const [editBillingAddressModal, showEditBillingAddressModal] = useModalTwoPromise<
        EditBillingAdressModalInputs,
        FullBillingAddress
    >(() => ({
        // Technically type casting isn't safe here, but practically speaking openBillingAddressModal ensures that these
        // properties are set correctly before the modal is shown.
        initialFullBillingAddress: {} as FullBillingAddress,
        subscription: undefined,
        source: 'invoices',
    }));

    const { paymentsApi: defaultPaymentsApi } = usePaymentsApi();
    const { createNotification } = useNotifications();
    const [loadingByKey, withLoadingByKey] = useLoadingByKey();

    const fetchBillingAddress = async (props?: Partial<EditBillingAdressModalInputs>) => {
        try {
            const paymentsApi = props?.paymentsApi ?? defaultPaymentsApi;
            return await paymentsApi.getFullBillingAddress();
        } catch (error) {
            createNotification({
                type: 'error',
                text: c('Error').t`Editing billing address is not available at the moment. Please try again later.`,
            });
            return Promise.reject(error);
        }
    };

    const openBillingAddressModal = async (
        props: Pick<EditBillingAdressModalInputs, 'subscription' | 'paymentsApi' | 'source'> & {
            loadingKey: string;
            taxCountry?: TaxCountryHook;
            vatNumber?: VatNumberHook;
        }
    ): Promise<FullBillingAddress> => {
        const { loadingKey, ...rest } = props;

        let fullBillingAddressBackend: FullBillingAddress;
        try {
            const promise = fetchBillingAddress(rest);
            void withLoadingByKey(loadingKey, promise);
            fullBillingAddressBackend = await promise;
        } catch (error) {
            return Promise.reject(error);
        }

        const result = await showEditBillingAddressModal({
            ...rest,
            initialFullBillingAddress: fullBillingAddressBackend,
        });

        void props.vatNumber?.vatUpdatedInModal(result);
        props.taxCountry?.billingAddressChangedInModal(result.BillingAddress);

        return result;
    };

    return {
        editBillingAddressModal: editBillingAddressModal((props) => {
            return <EditBillingAddressModal {...props} />;
        }),
        openBillingAddressModal,
        loadingByKey,
    };
};
